// src/admin/pages/TrafficPage.js
// Traffic page showing only activity data (renamed from UserAnalyticsPage)

import React, { useState, useEffect } from 'react';
import { 
  TrendingUp,
  Activity,
  BarChart3,
  Calendar,
  RefreshCw
} from 'lucide-react';
import ApiService from '../../services/apiService';

const TrafficPage = () => {
  const [analytics, setAnalytics] = useState({
    activityStats: null,
    pageAnalytics: null
  });
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [activityStatsResponse, pageAnalyticsResponse] = await Promise.all([
        ApiService.fetchActivityAnalytics(),
        ApiService.fetchPageAnalytics(30)
      ]);

      setAnalytics({
        activityStats: activityStatsResponse.success ? activityStatsResponse.data : null,
        pageAnalytics: pageAnalyticsResponse.success ? pageAnalyticsResponse.data : null
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setAnalytics({
        activityStats: null,
        pageAnalytics: null
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadAnalytics, 60000); // Refresh every minute
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const getColorClass = (color) => {
    const colorMap = {
      blue: 'text-blue-500',
      green: 'text-green-500',
      purple: 'text-purple-500',
      red: 'text-red-500',
      orange: 'text-orange-500',
      yellow: 'text-yellow-500'
    };
    return colorMap[color] || 'text-blue-500';
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white shadow rounded-lg p-5">
      <div className="flex items-center">
        <Icon className={`h-6 w-6 ${getColorClass(color)}`} />
        <h3 className="ml-3 text-lg font-medium text-gray-900">{title}</h3>
      </div>
      <p className="mt-4 text-3xl font-semibold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-4 text-lg text-gray-700">Loading traffic data...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Traffic</h1>
        <div className="flex items-center space-x-4">
          <label className="flex items-center text-gray-700">
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-600"
              checked={autoRefresh}
              onChange={() => setAutoRefresh(!autoRefresh)}
            />
            <span className="ml-2">Auto Refresh</span>
          </label>
          <button
            onClick={loadAnalytics}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            title="Daily Active"
            value={analytics.activityStats?.dailyActive || '0'}
            subtitle="Users today"
            icon={Activity}
            color="blue"
          />
          <MetricCard
            title="Weekly Active"
            value={analytics.activityStats?.weeklyActive || '0'}
            subtitle="Users this week"
            icon={Calendar}
            color="green"
          />
          <MetricCard
            title="Monthly Active"
            value={analytics.activityStats?.monthlyActive || '0'}
            subtitle="Users this month"
            icon={TrendingUp}
            color="purple"
          />
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Viewed Pages</h3>
          {analytics.pageAnalytics?.topPages && analytics.pageAnalytics.topPages.length > 0 ? (
            <div className="space-y-3">
              {analytics.pageAnalytics.topPages.map((page, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{page.name || page.path}</p>
                    <p className="text-sm text-gray-500">{page.path}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{page.views}</p>
                    <p className="text-xs text-gray-500">views</p>
                    {page.avgTime > 0 && (
                      <p className="text-xs text-gray-400 mt-1">{page.avgTime}s avg</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No page view data yet. Page tracking will start once users navigate the app.</p>
              <p className="text-sm text-gray-400 mt-2">Note: page_views table needs to be created in Supabase</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrafficPage;

